import type {
  InitialProfileStateType,
  NotificationType,
  WorkOrderEntity,
} from 'src/redux/profile/types';

export interface GroupedNotificationKey {
  senderName: string;
  workOrderId: string;
  type: string;
}

export interface GroupedNotification extends NotificationType {
  groupedCount: number;
  groupedNotificationIds: string[];
  latestMessageTimestamp: string;
}

/**
 * Creates a unique key for grouping vendor message notifications
 * Groups by sender name, work order ID, and notification type
 */
export const createNotificationGroupKey = (notification: NotificationType): string => {
  if (notification.type === 'vendor.message.received') {
    const senderName = notification.message?.sender_name;
    const workOrderId = notification.work_order?.work_order_id;

    // Only create a group key if we have both sender and work order
    if (!senderName || !workOrderId) {
      return ''; // Don't group if missing critical data
    }

    return `${notification.type}:${senderName}:${workOrderId}`;
  }
  return '';
};

/**
 * Checks if a notification should be grouped
 */
export const shouldGroupNotification = (notification: NotificationType): boolean =>
  notification.type === 'vendor.message.received';

/**
 * Finds an existing notification that should be grouped with the new one
 */
export const findGroupableNotification = (
  newNotification: NotificationType,
  notificationSource: { [key: string]: NotificationType },
): string | null => {
  if (!shouldGroupNotification(newNotification)) {
    return null;
  }

  const newGroupKey = createNotificationGroupKey(newNotification);
  if (!newGroupKey) return null;

  // Find existing notification with the same group key
  const foundEntry = Object.entries(notificationSource).find(
    ([_notificationId, existingNotification]) => {
      if (shouldGroupNotification(existingNotification)) {
        const existingGroupKey = createNotificationGroupKey(existingNotification);
        return existingGroupKey === newGroupKey;
      }
      return false;
    },
  );

  if (foundEntry) {
    return foundEntry[0]; // Return the notificationId
  }

  return null;
};

/**
 * Creates a grouped notification from an existing notification and a new one
 */
export const createGroupedNotification = (
  existingNotification: NotificationType,
  newNotification: NotificationType,
): GroupedNotification => {
  // Cast to GroupedNotification to access grouped fields
  const existing = existingNotification as GroupedNotification;

  return {
    ...existingNotification,
    read_at: newNotification.read_at,
    cleared_at: newNotification.cleared_at,
    // Update timestamp to the latest message
    created_at: newNotification.created_at,
    // Increment the count
    groupedCount: (existing.groupedCount || 1) + 1,
    // Add the new notification ID to the grouped list
    groupedNotificationIds: [
      ...(existing.groupedNotificationIds || [existingNotification.notification_id]),
      newNotification.notification_id,
    ],
    // Track the latest message timestamp
    latestMessageTimestamp: newNotification.created_at,
    // Keep the latest message data for display
    message: newNotification.message,
  };
};

/** A `sort` request param asks for oldest-first (`created_at`, not `-created_at`). */
export const isAscendingSort = (sort: unknown): boolean =>
  typeof sort === 'string' && sort !== '' && !sort.startsWith('-');

/**
 * Work order states whose work is done; mirrors the backend's LiveUrgentJob::WORK_DONE_STATES.
 * Only the fallback below reads it: live payloads that don't carry `on_live_urgent_job`.
 */
const FINISHED_WORK_ORDER_STATES = [
  'canceled',
  'closed',
  'resolved',
  'external',
  'completed',
  'ready_to_invoice',
  'quality_check',
];

/**
 * Demo (ac-66, D39 "emergencies first, then by time"): the server lists rows on a live
 * urgent job first under any sort. The row's own `on_live_urgent_job` (FU-28: every list
 * row, and the live payloads of job-tied items) decides. Otherwise the row's work order,
 * with gaps filled from the loaded work order (`workOrderEntities`, kept current by status
 * events): about half the work-order broadcasts carry no priority. A row whose priority
 * is still unknown counts as not urgent until the next fetch.
 */
export const isOnLiveUrgentJob = (
  notification?: NotificationType,
  workOrderEntities?: { [workOrderId: string]: WorkOrderEntity },
): boolean => {
  if (typeof notification?.on_live_urgent_job === 'boolean') {
    return notification.on_live_urgent_job;
  }
  const workOrder = notification?.work_order;
  const loaded = workOrder?.work_order_id
    ? workOrderEntities?.[workOrder.work_order_id]
    : undefined;
  const priority = workOrder?.priority?.value ?? loaded?.priority?.value;
  const state = workOrder?.status?.value ?? loaded?.status?.value ?? '';
  return priority === 'urgent' && !FINISHED_WORK_ORDER_STATES.includes(state);
};

/**
 * Put ONE row into a list that is already in the server's order, without
 * re-sorting the other rows. The row goes before the first neighbour it outranks by
 * `created_at` in the list's direction, or at the end. Used for a live (Pusher) row,
 * an un-cleared row and a newly cleared row.
 * Demo: a row on a live urgent job outranks every other row and never outranks one, so
 * it lands inside the top urgent block and a non-urgent row lands below it.
 */
export const placeIdByCreatedAt = (
  ids: string[],
  id: string,
  source: { [key: string]: NotificationType },
  ascending = false,
  workOrderEntities?: { [workOrderId: string]: WorkOrderEntity },
): string[] => {
  const time = (key: string) => new Date(source[key]?.created_at || 0).getTime();
  const urgent = (key: string) => isOnLiveUrgentJob(source[key], workOrderEntities);
  const rest = ids.filter((other) => other !== id);
  const at = rest.findIndex((other) => {
    if (urgent(other) !== urgent(id)) return urgent(id);
    return ascending ? time(other) > time(id) : time(other) < time(id);
  });
  return at === -1 ? [...rest, id] : [...rest.slice(0, at), id, ...rest.slice(at)];
};

/** Marks a placeholder id; never a real notification id. */
const MEMBER_SLOT_PREFIX = '\u0000member:';

/**
 * The two notifications-list slice fields that the `getNotification().fulfilled`
 * reducer and the server-rendered `/work-queues` page both produce.
 */
interface NotificationsListSlice {
  ids: InitialProfileStateType['notifications']['data'];
  source: InitialProfileStateType['notificationSource'];
}

/**
 * Build the settled `{ ids, source }` pair for the notifications list from one
 * page of rows.
 *
 * APX-522: lifted verbatim out of the `getNotification().fulfilled` case in
 * `src/redux/profile/extra-reducers.ts` so the Redux reducer and the
 * server-side read (`src/server/work-queue-rows.ts`) produce the same ids, the
 * same grouping and the same order BY CONSTRUCTION rather than by review. The
 * only substitutions made during the move are
 * `state.notifications.data` -> `existing.ids` and
 * `state.notificationSource` -> `existing.source`.
 *
 * Paginate-vs-fresh is expressed by WHAT the caller passes as `existing` — a
 * seeded pair appends, `{ ids: [], source: {} }` replaces — rather than by an
 * `isPaginating` boolean, so "page 1 reads no prior state" is explicit at every
 * call site instead of hidden in a flag.
 *
 * IMMER CONTRACT — when called from the reducer, `existing.source` holds Immer
 * DRAFT proxies of the current slice. This function only READS them and re-packs
 * them into fresh containers, which the reducer assigns straight back to
 * `state`, where Immer finalizes them. That is valid ONLY when the call is
 * SYNCHRONOUS and INSIDE the producer. Never `await` around this call, never
 * cache or defer the returned `source`, and never hand it to anything that
 * outlives the reducer: Immer revokes the proxies when the producer returns and
 * every later read would throw.
 */
export const buildNotificationsListState = (
  rows: NotificationType[],
  existing: NotificationsListSlice,
): NotificationsListSlice => {
  // The shallow copies stay INSIDE this function — deliberately NOT hoisted to
  // the call site — so the reducer keeps passing the draft and this code keeps
  // spreading it exactly as the previously-inline block did. That is what makes
  // the extraction behaviour-preserving under Immer.
  const newSource: NotificationsListSlice['source'] = { ...existing.source };
  const newIds: NotificationsListSlice['ids'] = [...existing.ids];

  // Group map to track vendor messages from new page only
  const groupMap = new Map<string, NotificationType[]>();
  // The list keeps the SERVER's order (it owns the sort, including
  // `sort=created_at` and the default newest-first). Each group member reserves a
  // slot where it appeared in the page; the second pass puts the group's row in its
  // NEWEST member's slot (the time the row shows) and drops the other slots.
  const memberSlot = (notificationId: string) => `${MEMBER_SLOT_PREFIX}${notificationId}`;

  // First pass: collect all NEW notifications and group vendor messages
  rows.forEach((item: NotificationType) => {
    if (shouldGroupNotification(item)) {
      const groupKey = createNotificationGroupKey(item);
      if (groupKey) {
        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey)?.push(item);
        newIds.push(memberSlot(item.notification_id));
      } else {
        // Can't group (missing sender/work_order), add normally
        if (item?.notification_id && newIds?.includes(item?.notification_id)) {
          newIds.splice(newIds.indexOf(item?.notification_id), 1);
        }
        newIds.push(item?.notification_id);
        newSource[item?.notification_id] = item;
      }
    } else {
      // Non-vendor message notification, add normally
      if (item?.notification_id && newIds?.includes(item?.notification_id)) {
        newIds.splice(newIds.indexOf(item?.notification_id), 1);
      }
      newIds.push(item?.notification_id);
      newSource[item?.notification_id] = item;
    }
  });

  // Second pass: process grouped vendor messages
  groupMap.forEach((notifications) => {
    if (notifications.length > 0) {
      // Sort by created_at to get the latest message
      const sortedNotifications = notifications.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      // Use the first (latest) notification as the main one
      const mainNotification = sortedNotifications[0];
      const groupedNotificationIds = notifications.map((n) => n.notification_id);

      // Create grouped notification
      const groupedNotification = {
        ...mainNotification,
        groupedCount: notifications.length,
        groupedNotificationIds,
        latestMessageTimestamp: mainNotification.created_at,
      };

      // Fill the group's slot; a copy left by an earlier page moves here, like a
      // repeated ungrouped id does in the first pass.
      const staleIndex = newIds.indexOf(mainNotification.notification_id);
      if (staleIndex !== -1) {
        newIds.splice(staleIndex, 1);
      }
      newIds[newIds.indexOf(memberSlot(mainNotification.notification_id))] =
        mainNotification.notification_id;
      newSource[mainNotification.notification_id] = groupedNotification;
    }
  });

  return { ids: newIds.filter((id) => !id.startsWith(MEMBER_SLOT_PREFIX)), source: newSource };
};

/**
 * Gets the display text for grouped notifications
 */
export const getGroupedNotificationText = (notification: GroupedNotification): string => {
  const senderName = notification.message?.sender_name || 'Unknown';
  const count = notification.groupedCount || 1;

  if (count > 1) {
    return `${count} new messages from ${senderName}`;
  }
  return `New message from ${senderName}`;
};
